import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X, User, LogOut, LayoutDashboard, CalendarCheck, ChevronDown, Wrench, Car, Sparkles, Phone, Truck, MapPin, FileText, CreditCard, Lock, Shield, HelpCircle } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const LOGO_URL = "https://customer-assets-rejwkqb3.emergentagent.net/job_47615226-8be8-492e-9b64-c74bd12fbe55/artifacts/a0f8zaxp_02_MA_Africa_Detailing_Full_Colour_Transparent.webp";

const navLinks = [
  { 
    name: 'Fix', 
    path: '/fix',
    submenu: [
      { name: 'Mechanical Repairs', path: '/mechanical', icon: Wrench },
      { name: 'Panel Beating', path: '/panel-beating', icon: Car },
    ]
  },
  { 
    name: 'Detail', 
    path: '/detail',
    submenu: [
      { name: 'All Packages', path: '/detailing', icon: Sparkles },
      { name: 'Paint Correction', path: '/paint-correction', icon: Sparkles },
      { name: 'Ceramic Coating', path: '/ceramic-coating', icon: Sparkles },
    ]
  },
  { name: 'Fleet', path: '/fleet-services', icon: Truck },
  { name: 'Track', path: '/track', icon: MapPin },
  { name: 'Our Work', path: '/gallery' },
  { 
    name: 'Support', 
    path: '/contact-us',
    submenu: [
      { name: 'Contact Us', path: '/contact-us', icon: Phone },
      { name: 'Payment Info', path: '/payment-info', icon: CreditCard },
      { name: 'Terms & Conditions', path: '/terms', icon: FileText },
      { name: 'Refund Policy', path: '/refund-policy', icon: Shield },
      { name: 'Privacy Policy', path: '/privacy-policy', icon: Lock },
      { name: 'Service Policy', path: '/service-policy', icon: HelpCircle },
    ]
  },
];

export default function Navbar() {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [activeSubmenu, setActiveSubmenu] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const location = useLocation();
  const { user, loading, login, logout } = useAuth();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 10);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsOpen(false);
    setUserMenuOpen(false);
    setActiveSubmenu(null);
  }, [location]);

  // Check admin status when user changes
  useEffect(() => {
    if (user) {
      checkAdminStatus();
    } else {
      setIsAdmin(false);
    }
  }, [user]);

  const checkAdminStatus = async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/check`, {
        credentials: 'include'
      });
      const data = await response.json();
      setIsAdmin(data.is_admin || false);
    } catch {
      setIsAdmin(false);
    }
  };

  return (
    <nav 
      data-testid="navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? 'bg-white/95 backdrop-blur-md border-b border-gray-200 shadow-sm' : 'bg-transparent'
      }`}
    >
      <div className="max-w-[1200px] mx-auto px-6">
        <div className="flex items-center justify-between h-16 md:h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center" data-testid="logo-link">
            <img 
              src={LOGO_URL} 
              alt="MA Africa Fix & Detail" 
              className="h-10 md:h-12 w-auto"
            />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-1">
            {navLinks.map((link) => (
              <div 
                key={link.name} 
                className="relative"
                onMouseEnter={() => link.submenu && setActiveSubmenu(link.name)}
                onMouseLeave={() => setActiveSubmenu(null)}
              >
                {link.submenu ? (
                  <>
                    <button
                      className={`flex items-center gap-1 px-4 py-2 text-sm font-medium transition-colors rounded-lg ${
                        scrolled ? 'text-[#424245] hover:text-[#1d1d1f] hover:bg-gray-100' : 'text-white/90 hover:text-white hover:bg-white/10'
                      }`}
                      data-testid={`nav-${link.name.toLowerCase()}`}
                    >
                      {link.name}
                      <ChevronDown className="w-4 h-4" />
                    </button>
                    
                    <AnimatePresence>
                      {activeSubmenu === link.name && (
                        <motion.div
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, y: 10 }}
                          className="absolute top-full left-0 mt-1 w-56 bg-white rounded-xl shadow-xl border border-gray-100 py-2 overflow-hidden"
                        >
                          {link.submenu.map((subItem) => (
                            <Link
                              key={subItem.name}
                              to={subItem.path}
                              className="flex items-center gap-3 px-4 py-3 text-sm text-[#424245] hover:text-[#C41E3A] hover:bg-gray-50 transition-colors"
                            >
                              <subItem.icon className="w-4 h-4" />
                              {subItem.name}
                            </Link>
                          ))}
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </>
                ) : (
                  <Link
                    to={link.path}
                    data-testid={`nav-${link.name.toLowerCase().replace(' ', '-')}`}
                    className={`px-4 py-2 text-sm font-medium transition-colors rounded-lg ${
                      location.pathname === link.path
                        ? scrolled ? 'text-[#C41E3A]' : 'text-white'
                        : scrolled ? 'text-[#424245] hover:text-[#1d1d1f] hover:bg-gray-100' : 'text-white/90 hover:text-white hover:bg-white/10'
                    }`}
                  >
                    {link.name}
                  </Link>
                )}
              </div>
            ))}
          </div>

          {/* Desktop Right Section */}
          <div className="hidden lg:flex items-center gap-3">
            <Link
              to="/repair-quote"
              data-testid="nav-repair-quote"
              className={`text-sm font-medium px-4 py-2 rounded-lg transition-colors ${
                scrolled ? 'text-[#424245] hover:text-[#1d1d1f] hover:bg-gray-100' : 'text-white/90 hover:text-white hover:bg-white/10'
              }`}
            >
              Get Quote
            </Link>
            <Link
              to="/booking"
              data-testid="cta-book-now"
              className="bg-[#C41E3A] text-white text-sm font-semibold px-5 py-2.5 rounded-lg hover:bg-[#a01830] transition-colors"
            >
              Book Now
            </Link>
            
            {/* Auth Section */}
            {!loading && (
              <>
                {user ? (
                  <div className="relative ml-2">
                    <button
                      onClick={() => setUserMenuOpen(!userMenuOpen)}
                      className={`flex items-center gap-2 text-sm transition-colors p-1.5 rounded-lg ${
                        scrolled ? 'text-[#424245] hover:bg-gray-100' : 'text-white/90 hover:bg-white/10'
                      }`}
                      data-testid="user-menu-button"
                    >
                      {user.picture ? (
                        <img 
                          src={user.picture} 
                          alt={user.name} 
                          className="w-8 h-8 rounded-full object-cover border-2 border-white/20"
                        />
                      ) : (
                        <div className="w-8 h-8 rounded-full bg-[#C41E3A] flex items-center justify-center">
                          <User className="w-4 h-4 text-white" />
                        </div>
                      )}
                    </button>
                    
                    <AnimatePresence>
                      {userMenuOpen && (
                        <motion.div
                          initial={{ opacity: 0, y: -10 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, y: -10 }}
                          className="absolute right-0 top-full mt-2 w-56 bg-white rounded-xl shadow-xl border border-gray-100 py-2"
                          data-testid="user-dropdown"
                        >
                          <div className="px-4 py-3 border-b border-gray-100">
                            <p className="text-sm font-semibold text-[#1d1d1f] truncate">{user.name}</p>
                            <p className="text-xs text-[#86868b] truncate">{user.email}</p>
                          </div>
                          <Link
                            to="/my-bookings"
                            className="flex items-center gap-3 px-4 py-3 text-sm text-[#424245] hover:text-[#C41E3A] hover:bg-gray-50 transition-colors"
                            data-testid="my-bookings-link"
                          >
                            <CalendarCheck className="w-4 h-4" />
                            My Bookings
                          </Link>
                          {isAdmin && (
                            <Link
                              to="/admin"
                              className="flex items-center gap-3 px-4 py-3 text-sm text-[#C41E3A] hover:bg-gray-50 transition-colors"
                              data-testid="admin-link"
                            >
                              <LayoutDashboard className="w-4 h-4" />
                              Admin Dashboard
                            </Link>
                          )}
                          <button
                            onClick={logout}
                            className="flex items-center gap-3 w-full px-4 py-3 text-sm text-[#424245] hover:text-[#C41E3A] hover:bg-gray-50 transition-colors"
                            data-testid="logout-button"
                          >
                            <LogOut className="w-4 h-4" />
                            Sign out
                          </button>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                ) : (
                  <div className="relative ml-2 flex items-center gap-2">
                    <Link
                      to="/login"
                      className={`text-sm font-medium px-3 py-1.5 rounded-lg transition-colors ${
                        scrolled ? 'text-[#424245] hover:bg-gray-100' : 'text-white/90 hover:bg-white/10'
                      }`}
                      data-testid="login-link"
                    >
                      Login
                    </Link>
                    <Link
                      to="/signup"
                      className="text-sm font-medium px-4 py-1.5 rounded-lg bg-[#C41E3A] text-white hover:bg-[#a01830] transition-colors"
                      data-testid="signup-link"
                    >
                      Sign Up
                    </Link>
                  </div>
                )}
              </>
            )}
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsOpen(!isOpen)}
            className={`lg:hidden p-2 rounded-lg ${
              scrolled ? 'text-[#1d1d1f] hover:bg-gray-100' : 'text-white hover:bg-white/10'
            }`}
            data-testid="mobile-menu-toggle"
          >
            {isOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="lg:hidden bg-white border-t border-gray-100"
            data-testid="mobile-menu"
          >
            <div className="max-w-[1200px] mx-auto px-6 py-4 space-y-1">
              {navLinks.map((link) => (
                <div key={link.name}>
                  {link.submenu ? (
                    <div className="space-y-1">
                      <p className="px-4 py-2 text-xs font-semibold text-[#86868b] uppercase tracking-wider">
                        {link.name}
                      </p>
                      {link.submenu.map((subItem) => (
                        <Link
                          key={subItem.name}
                          to={subItem.path}
                          data-testid={`mobile-nav-${subItem.name.toLowerCase().replace(' ', '-')}`}
                          className="flex items-center gap-3 px-4 py-3 text-sm text-[#424245] hover:text-[#C41E3A] hover:bg-gray-50 rounded-lg transition-colors"
                        >
                          <subItem.icon className="w-4 h-4" />
                          {subItem.name}
                        </Link>
                      ))}
                    </div>
                  ) : (
                    <Link
                      to={link.path}
                      data-testid={`mobile-nav-${link.name.toLowerCase().replace(' ', '-')}`}
                      className={`block px-4 py-3 text-sm font-medium rounded-lg transition-colors ${
                        location.pathname === link.path
                          ? 'text-[#C41E3A] bg-[#C41E3A]/5'
                          : 'text-[#424245] hover:text-[#1d1d1f] hover:bg-gray-50'
                      }`}
                    >
                      {link.name}
                    </Link>
                  )}
                </div>
              ))}
              
              <div className="pt-4 space-y-2 border-t border-gray-100">
                <Link
                  to="/repair-quote"
                  className="block px-4 py-3 text-sm font-medium text-[#424245] hover:text-[#C41E3A] hover:bg-gray-50 rounded-lg transition-colors"
                >
                  Get a Repair Quote
                </Link>
                <Link
                  to="/booking"
                  className="block w-full text-center bg-[#C41E3A] text-white py-3 rounded-lg font-semibold hover:bg-[#a01830] transition-colors"
                >
                  Book Now
                </Link>
              </div>
              
              {/* Mobile Auth */}
              {!loading && (
                <div className="pt-4 border-t border-gray-100">
                  {user ? (
                    <div className="space-y-1">
                      <div className="flex items-center gap-3 px-4 py-3">
                        {user.picture ? (
                          <img 
                            src={user.picture} 
                            alt={user.name} 
                            className="w-10 h-10 rounded-full object-cover"
                          />
                        ) : (
                          <div className="w-10 h-10 rounded-full bg-[#C41E3A] flex items-center justify-center">
                            <User className="w-5 h-5 text-white" />
                          </div>
                        )}
                        <div>
                          <p className="text-sm font-semibold text-[#1d1d1f]">{user.name}</p>
                          <p className="text-xs text-[#86868b]">{user.email}</p>
                        </div>
                      </div>
                      <Link
                        to="/my-bookings"
                        className="flex items-center gap-3 px-4 py-3 text-sm text-[#424245] hover:text-[#C41E3A] hover:bg-gray-50 rounded-lg"
                        data-testid="mobile-my-bookings-link"
                      >
                        <CalendarCheck className="w-4 h-4" />
                        My Bookings
                      </Link>
                      {isAdmin && (
                        <Link
                          to="/admin"
                          className="flex items-center gap-3 px-4 py-3 text-sm text-[#C41E3A] hover:bg-gray-50 rounded-lg"
                          data-testid="mobile-admin-link"
                        >
                          <LayoutDashboard className="w-4 h-4" />
                          Admin Dashboard
                        </Link>
                      )}
                      <button
                        onClick={logout}
                        className="flex items-center gap-3 w-full px-4 py-3 text-sm text-[#424245] hover:text-[#C41E3A] hover:bg-gray-50 rounded-lg"
                        data-testid="mobile-logout-button"
                      >
                        <LogOut className="w-4 h-4" />
                        Sign out
                      </button>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      <Link
                        to="/login"
                        className="flex items-center gap-3 w-full px-4 py-3 text-sm text-[#424245] hover:text-[#C41E3A] hover:bg-gray-50 rounded-lg"
                        data-testid="mobile-login-link"
                        onClick={() => setIsOpen(false)}
                      >
                        <User className="w-4 h-4" />
                        Login
                      </Link>
                      <Link
                        to="/signup"
                        className="flex items-center justify-center gap-2 w-full px-4 py-3 text-sm font-medium text-white bg-[#C41E3A] hover:bg-[#a01830] rounded-lg"
                        data-testid="mobile-signup-link"
                        onClick={() => setIsOpen(false)}
                      >
                        Sign Up
                      </Link>
                    </div>
                  )}
                </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </nav>
  );
}
